import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * V2 TopActionsSection — Liste prioritaire des créateurs nécessitant une action.
 *
 * NoiRock adaptation Phase 1 (R9 : pas de scraping) :
 *  - Pas d'alertes violations / banishes auto (scraping-dependent — Phase 2).
 *  - On affiche les créateurs *sous-actifs* (sessions = 0 ou valid_go_live_days
 *    sous le seuil pour la période en cours) ainsi que les "onboarding" (joined
 *    depuis < 14j).
 *
 * Props :
 *  - actions : array<{ creator_id, display_name, kind: 'inactive'|'onboarding'|'risk', server, reason }>
 *  - criticalCount : nb total d'actions critiques
 *  - loading
 */
const KIND_BADGE = {
  inactive:   { label: 'inactive',   cls: 'bg-warning/10 text-warning' },
  onboarding: { label: 'onboarding', cls: 'bg-primary/10 text-primary' },
  risk:       { label: 'risk',       cls: 'bg-error/10 text-error' },
};

const TopActionsSection = ({ actions = [], criticalCount = 0, loading = false, limit = 5, showSeeAll = true }) => {
  const { t } = useTranslation();

  return (
    <section className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-surface-2 flex items-center justify-between">
        <div className="font-heading font-semibold text-foreground text-sm">
          {t('managerV2.topActions.title', 'Top actions')}
          {criticalCount > 0 && (
            <span className="ml-2 text-xs text-error font-bold tabular-nums">{criticalCount}</span>
          )}
        </div>
        {showSeeAll && (
          <Link to="/manager/actions" className="text-xs text-primary hover:underline">
            {t('managerV2.topActions.seeAll', 'See all')} →
          </Link>
        )}
      </div>

      {loading ? (
        <div className="h-32 skeleton" />
      ) : actions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {t('managerV2.topActions.empty', 'No actions for now.')}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {actions.slice(0, limit).map((a) => {
            const badge = KIND_BADGE[a.kind] || KIND_BADGE.inactive;
            const label = t(`managerV2.topActions.kind.${a.kind}`, badge.label);
            return (
              <li key={a.creator_id} className="px-5 py-3 hover:bg-muted/40 transition-colors">
                <Link to={`/manager/team/${a.creator_id}`} className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${badge.cls}`}>
                    {label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">{a.display_name}</div>
                    {a.reason && <div className="text-xs text-muted-foreground truncate">{a.reason}</div>}
                  </div>
                  <span className="text-xs text-muted-foreground">{a.server || ''}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default TopActionsSection;
